import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Filter,
  ArrowUpNarrowWide,
  ArrowDownWideNarrow,
  Link as LinkIcon,
  ChevronDown,
  Plus,
  Minus,
  X,
} from "lucide-react";
import { listAllPreQuotedCooks, getApprovedQtyPQC } from "../../../../reducers/prequotedcooks.slice";
import { createOrder } from "../../../../reducers/order.slice";
import { formatNumberToDollar, countActiveFiltersAndSort } from "../../../../utils/util";
import { AuthContext } from "../../../../context/authContext";
import { Button } from "../../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../../../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../../components/ui/dialog";
import PageHeader from "../../../../components/shared/PageHeader";
import StatusBadge from "../../../../components/shared/StatusBadge";
import SearchBar from "../../../../components/shared/SearchBar";
import RefreshButton from "../../../../components/shared/RefreshButton";
import DataTable from "../../../../components/shared/DataTable";
import ViewToggle from "../../../../components/shared/ViewToggle";
import FieldBlock from "../../../../components/shared/FieldBlock";
import CreateOrderAgreement from "../../../../components/shared/CreateOrderAgreement";
import useMediaQuery from "../../../../components/shared/useMediaQuery";

const STATUS_OPTIONS = ["ACTIVE", "INACTIVE"];
const FALLBACK_IMAGE = "https://cdn.pixabay.com/photo/2024/06/26/23/36/package-8856091_1280.png";
const ROWS_PER_PAGE_DEFAULT = 8;

const MemberPreQuotedCooksPage = () => {
  const [selectedCards, setSelectedCards] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState([]);
  const [preQuoteDetails, setPreQuoteDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const storedStatuses = sessionStorage.getItem("prequotedCooksMember_selectedStatuses");
    return storedStatuses ? JSON.parse(storedStatuses) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return sessionStorage.getItem("prequotedCooksMember_sortDirection") || null;
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("pqcViewMode") || "card");
  const [openViewImgDialogue, setOpenViewImgDialogue] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [page, setPage] = useState(Number(sessionStorage.getItem("tablePage")) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    Number(sessionStorage.getItem("tableRowsPerPage")) || ROWS_PER_PAGE_DEFAULT
  );

  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderLines, setOrderLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const handleViewChange = (newView) => {
    setViewMode(newView);
    localStorage.setItem("pqcViewMode", newView);
  };

  const getAllPQC = () => {
    setLoading(true);
    dispatch(listAllPreQuotedCooks()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const formattedPQC = res?.payload?.map((item) => {
        const buyPrice = formatNumberToDollar(item?.buy_price);
        const cashoutPrice = formatNumberToDollar(item?.cashout_price);
        return { ...item, buyPrice, cashoutPrice };
      });
      setLoading(false);
      setPreQuoteDetails(formattedPQC || []);
    });
  };

  useEffect(() => {
    getAllPQC();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setSearchResults(preQuoteDetails);
  }, [preQuoteDetails]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!debouncedSearch || !debouncedSearch.trim()) {
        setSearchResults(preQuoteDetails);
        return;
      }
      const query = debouncedSearch.toLowerCase();
      setSearchResults(
        preQuoteDetails.filter((row) =>
          ["upc", "retailer_name", "item_name"].some((param) => {
            const value = row[param];
            return value && value.toString().toLowerCase().includes(query);
          })
        )
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [debouncedSearch, preQuoteDetails]);

  useEffect(() => {
    let filtered = [...searchResults];

    if (selectedStatuses?.length > 0) {
      filtered = filtered.filter((item) => selectedStatuses.includes(item?.status));
    }

    if (sortDirection === "asc" || sortDirection === "desc") {
      filtered.sort((a, b) => {
        const amountA = Number(a?.total_quantity || 0);
        const amountB = Number(b?.total_quantity || 0);
        return sortDirection === "asc" ? amountA - amountB : amountB - amountA;
      });
    }

    setFilteredData(filtered);
    setPage(0);
    sessionStorage.setItem("tablePage", 0);
  }, [searchResults, selectedStatuses, sortDirection]);

  useEffect(() => {
    sessionStorage.setItem("prequotedCooksMember_selectedStatuses", JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  useEffect(() => {
    if (sortDirection) {
      sessionStorage.setItem("prequotedCooksMember_sortDirection", sortDirection);
    } else {
      sessionStorage.removeItem("prequotedCooksMember_sortDirection");
    }
  }, [sortDirection]);

  const handleStatusChange = (status, checked) => {
    setSelectedStatuses((prev) => (checked ? [...prev, status] : prev.filter((s) => s !== status)));
  };

  const handleSortAscending = () => setSortDirection((prev) => prev === "asc" ? null : "asc");
  const handleSortDescending = () => setSortDirection((prev) => prev === "desc" ? null : "desc");

  const handleChangePage = (newPage) => {
    setPage(newPage);
    sessionStorage.setItem("tablePage", newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    sessionStorage.setItem("tableRowsPerPage", newRowsPerPage);
  };

  const toggleCardSelection = (card) => {
    if (card?.total_quantity - card?.available_quantity === 0) return;
    setSelectedCards((prev) => {
      const isCardSelected = prev.some((selectedCard) => selectedCard.id === card.id);
      return isCardSelected
        ? prev.filter((selectedCard) => selectedCard.id !== card.id)
        : [...prev, card];
    });
  };

  const handleAdd = (row) => setSelectedOrder((prev) => [...prev, row]);
  const handleRemove = (row) =>
    setSelectedOrder((prev) => prev.filter((selectedRow) => selectedRow.id !== row.id));

  const openImageDialog = (row) => {
    setSelectedRow(row);
    setOpenViewImgDialogue(true);
  };

  // Opens the "set quantities" dialog inline on this page instead of navigating
  // away — mirrors the artifact's catalog flow. Each line independently fetches
  // its real server-approved max quantity via getApprovedQtyPQC before the
  // stepper becomes usable, same validation the old confirm page enforced.
  const handleOpenOrderDialog = () => {
    const items = viewMode === "table" ? selectedOrder : selectedCards;
    if (!items.length) return;

    const lines = items.map((item) => ({
      id: item.id,
      item_name: item.item_name,
      item_link: item.item_link,
      upc: item.upc,
      retailer_name: item.retailer_name,
      buy_price: item.buy_price,
      cashout_price: item.cashout_price,
      cashoutPrice: item.cashoutPrice,
      quantity: 1,
      maxQty: null,
      loadingMax: true,
    }));
    setOrderLines(lines);
    setAgreementChecked(false);
    setOrderDialogOpen(true);

    items.forEach((item) => {
      dispatch(getApprovedQtyPQC({ id: item.id })).then((res) => {
        const allowed = Number(res?.payload?.allowed_quantity) || 0;
        setOrderLines((prev) =>
          prev.map((line) =>
            line.id === item.id
              ? { ...line, maxQty: allowed, loadingMax: false, quantity: allowed > 0 ? Math.min(line.quantity, allowed) : 0 }
              : line
          )
        );
      });
    });
  };

  const handleQtyStep = (id, delta) => {
    setOrderLines((prev) =>
      prev.map((line) => {
        if (line.id !== id || line.loadingMax) return line;
        const max = line.maxQty || 0;
        return { ...line, quantity: Math.max(0, Math.min(max, line.quantity + delta)) };
      })
    );
  };

  const handleQtyInputChange = (id, rawValue) => {
    const digits = rawValue.replace(/\D/g, "");
    setOrderLines((prev) =>
      prev.map((line) => {
        if (line.id !== id || line.loadingMax) return line;
        const max = line.maxQty || 0;
        const parsed = digits === "" ? 0 : parseInt(digits, 10);
        return { ...line, quantity: Math.max(0, Math.min(max, parsed)) };
      })
    );
  };

  const handleRemoveLine = (id) => {
    setOrderLines((prev) => prev.filter((line) => line.id !== id));
    setSelectedCards((prev) => prev.filter((card) => card.id !== id));
    setSelectedOrder((prev) => prev.filter((row) => row.id !== id));
  };

  const handleOrderDialogChange = (open) => {
    setOrderDialogOpen(open);
    if (!open) {
      setOrderLines([]);
      setAgreementChecked(false);
    }
  };

  const handleConfirmCreateOrder = () => {
    if (!orderLines.length) return;
    if (!agreementChecked) {
      showToast("Please agree to the order terms before creating the order.", "error");
      return;
    }
    const hasZeroQuantity = orderLines.some((line) => Number(line.quantity) === 0);
    if (hasZeroQuantity) {
      showToast("Quantity cannot be zero. Please update or remove all items.", "error");
      return;
    }

    setSubmitting(true);
    const order_items = orderLines.map((line) => ({
      item_name: line.item_name,
      item_link: line.item_link || "",
      upc: line.upc || "",
      quote_id: line.id || "",
      quantity: Number(line.quantity),
      buy_price: line.buy_price || 0,
      cashout_price: line.cashout_price || 0,
      total_amount: (line.cashout_price || 0) * Number(line.quantity),
      retailer_name: line.retailer_name || "",
    }));

    dispatch(createOrder({ type: "PRE_QUOTE", order_items })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setSubmitting(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setSubmitting(false);
      setOrderDialogOpen(false);
      setOrderLines([]);
      setAgreementChecked(false);
      setSelectedCards([]);
      setSelectedOrder([]);
      showToast(
        `Order created with ${order_items.length} item${order_items.length > 1 ? "s" : ""} — pending approval in Open Orders`,
        "success"
      );
      navigate("/dashboard/open-orders");
    });
  };

  const columns = [
    {
      key: "item_name",
      label: "Item Name",
      maxWidth: 160,
      render: (row) => (
        <a
          href={row?.item_link || "#"}
          target="_blank"
          rel="noopener noreferrer"
          title={row?.item_name}
          style={{ color: "#4fb8f0", textDecoration: "underline", fontSize: 12.5, cursor: "pointer" }}
          onClick={(e) => {
            if (!row?.item_link) {
              showToast("Item Link not available");
              e.preventDefault();
            }
          }}
        >
          {row?.item_name}
        </a>
      ),
    },
    { key: "upc", label: "UPC", maxWidth: 110 },
    { key: "retailer_name", label: "Retailer Name", maxWidth: 130 },
    {
      key: "quantity",
      label: "Quantity",
      render: (row) => `0/${row?.total_quantity - row?.available_quantity}`,
    },
    { key: "buyPrice", label: "Buy Price" },
    { key: "cashoutPrice", label: "Cashout Price" },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row?.status} />,
    },
    {
      key: "actions",
      label: "Action",
      align: "center",
      render: (row) => {
        const isSelected = selectedOrder.some((selectedRow) => selectedRow.id === row.id);
        const isDisabled = row?.total_quantity - row?.available_quantity === 0;
        return (
          <button
            type="button"
            aria-label={isSelected ? "remove item" : "add item"}
            disabled={isDisabled}
            onClick={(e) => {
              e.stopPropagation();
              isSelected ? handleRemove(row) : handleAdd(row);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: 8,
              background: "transparent",
              border: `1.5px solid ${isDisabled ? "#3a3a3e" : isSelected ? "#f27059" : "#4caf50"}`,
              color: isDisabled ? "#3a3a3e" : isSelected ? "#f27059" : "#4caf50",
              cursor: isDisabled ? "not-allowed" : "pointer",
            }}
          >
            {isSelected ? <Minus size={13} /> : <Plus size={13} />}
          </button>
        );
      },
    },
  ];

  const customNestedComponent = (row) => (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 10,
        background: "rgba(255,255,255,0.02)",
        padding: 16,
        marginTop: 4,
      }}
    >
      <div style={{ flex: "1 1 260px", marginRight: isMobile ? 0 : 16, marginBottom: isMobile ? 14 : 0, minWidth: 0 }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
          <FieldBlock label="ITEM NAME" value={row?.item_name || "N/A"} />
          <FieldBlock label="UPC" value={row?.upc || "N/A"} />
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", color: "#c3c8cc", marginBottom: 4 }}>
            DESCRIPTION
          </div>
          <div style={{ fontSize: 12.5, color: "#c3c8cc", lineHeight: 1.5 }}>
            {row?.description || "No description available"}
          </div>
        </div>
      </div>
      <div
        onClick={() => openImageDialog(row)}
        style={{
          width: isMobile ? "100%" : 180,
          height: 140,
          borderRadius: 8,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.08)",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <img
          src={row?.item_image_url || FALLBACK_IMAGE}
          alt={row?.item_name}
          style={{ width: "100%", height: "100%", objectFit: "contain", background: "#fff" }}
        />
      </div>
    </div>
  );

  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedStatuses);
  const pagedRows = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const selectedCount = (viewMode === "table" ? selectedOrder : selectedCards).length;
  const orderTotal = orderLines.reduce((sum, line) => sum + (line.cashout_price || 0) * (line.quantity || 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Dialog open={openViewImgDialogue} onOpenChange={setOpenViewImgDialogue}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedRow?.item_name || "Item image"}</DialogTitle>
            {selectedRow?.description && <DialogDescription>{selectedRow.description}</DialogDescription>}
          </DialogHeader>
          <div
            style={{
              width: "100%",
              height: isMobile ? 240 : 320,
              borderRadius: 10,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.08)",
              background: "#fff",
            }}
          >
            <img
              src={selectedRow?.item_image_url || FALLBACK_IMAGE}
              alt={selectedRow?.item_name}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={orderDialogOpen} onOpenChange={handleOrderDialogChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create order — set quantities</DialogTitle>
            <DialogDescription>
              Confirm how many units of each item you're sending, then create your order.
            </DialogDescription>
          </DialogHeader>

          <div style={{ maxHeight: 340, overflowY: "auto" }}>
            {orderLines.map((line) => (
              <div
                key={line.id}
                style={{
                  display: "flex",
                  alignItems: isMobile ? "stretch" : "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexDirection: isMobile ? "column" : "row",
                  padding: "11px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <div style={{ minWidth: 0, marginRight: 12 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#fff",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {line.item_name}
                  </div>
                  <div style={{ fontSize: 11, color: "#c3c8cc", marginTop: 2 }}>
                    @ {line.cashoutPrice} cashout ·{" "}
                    {line.loadingMax
                      ? "checking allowed qty…"
                      : line.maxQty > 0
                        ? `${line.maxQty} available`
                        : "not available right now"}
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", flexShrink: 0, justifyContent: isMobile ? "space-between" : "flex-start", width: isMobile ? "100%" : "auto" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #2a2a2e",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    <button
                      type="button"
                      aria-label="decrease quantity"
                      disabled={line.loadingMax || line.quantity <= 0}
                      onClick={() => handleQtyStep(line.id, -1)}
                      style={{
                        width: 28,
                        height: 28,
                        background: "#1e1e1e",
                        border: "none",
                        color: line.loadingMax || line.quantity <= 0 ? "#4a4a4e" : "#fff",
                        fontSize: 15,
                        cursor: line.loadingMax || line.quantity <= 0 ? "not-allowed" : "pointer",
                      }}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={line.quantity}
                      disabled={line.loadingMax}
                      onChange={(e) => handleQtyInputChange(line.id, e.target.value)}
                      style={{
                        width: 44,
                        height: 28,
                        textAlign: "center",
                        background: "#000",
                        border: "none",
                        borderLeft: "1px solid #2a2a2e",
                        borderRight: "1px solid #2a2a2e",
                        color: "#fff",
                        fontFamily: "ui-monospace, monospace",
                        fontSize: 13,
                      }}
                    />
                    <button
                      type="button"
                      aria-label="increase quantity"
                      disabled={line.loadingMax || line.quantity >= (line.maxQty || 0)}
                      onClick={() => handleQtyStep(line.id, 1)}
                      style={{
                        width: 28,
                        height: 28,
                        background: "#1e1e1e",
                        border: "none",
                        color: line.loadingMax || line.quantity >= (line.maxQty || 0) ? "#4a4a4e" : "#fff",
                        fontSize: 15,
                        cursor: line.loadingMax || line.quantity >= (line.maxQty || 0) ? "not-allowed" : "pointer",
                      }}
                    >
                      +
                    </button>
                  </div>

                  <button
                    type="button"
                    aria-label="remove from order"
                    onClick={() => handleRemoveLine(line.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 26,
                      height: 26,
                      marginLeft: 8,
                      borderRadius: 7,
                      background: "transparent",
                      border: "none",
                      color: "#c3c8cc",
                      cursor: "pointer",
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              background: "#1e1e1e",
              borderRadius: 10,
              padding: "12px 14px",
              fontSize: 13,
              display: "flex",
              justifyContent: "space-between",
              marginTop: 14,
            }}
          >
            <span style={{ color: "#c3c8cc" }}>Estimated cashout</span>
            <b style={{ fontFamily: "ui-monospace, monospace", color: "#fff" }}>
              {formatNumberToDollar(orderTotal)}
            </b>
          </div>

          <CreateOrderAgreement
            checked={agreementChecked}
            onChange={setAgreementChecked}
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderDialogOpen(false)}>
              Back
            </Button>
            <div style={{ marginLeft: 10 }}>
              <Button
                onClick={handleConfirmCreateOrder}
                disabled={submitting || !agreementChecked || orderLines.length === 0 || orderLines.some((l) => l.loadingMax)}
              >
                {submitting ? "Creating…" : "Create order →"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div style={{ marginBottom: 20 }}>
        <PageHeader
          title="Sell"
          subtitle="Below are items you can sell to us at our pre-quoted price!"
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: isMobile ? "stretch" : "center",
          flexWrap: "wrap",
          marginBottom: 20,
        }}
      >
        <div style={{ display: "flex", alignItems: isMobile ? "stretch" : "center", flexWrap: "wrap", marginBottom: 8, width: isMobile ? "100%" : "auto", gap: isMobile ? 10 : 0 }}>
          {!isMobile && <RefreshButton onRefresh={getAllPQC} />}

          <div style={{ marginLeft: isMobile ? 0 : 12 }}>
            <ViewToggle viewMode={viewMode} onChange={handleViewChange} />
          </div>

          <div style={{ marginLeft: isMobile ? 0 : 12, flex: isMobile ? "1 1 0" : "none", minWidth: 0 }}>
            <SearchBar
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              setDebouncedTerm={setDebouncedSearch}
            />
          </div>

          <div style={{ marginLeft: isMobile ? 0 : 20, flexShrink: 0 }}>
            <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter size={13} style={{ marginRight: 6 }} />
                  Filter & Sort
                  {activeFilterCount > 0 && (
                    <span
                      style={{
                        marginLeft: 6,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 16,
                        height: 16,
                        borderRadius: 8,
                        background: "#00ADB5",
                        color: "#000",
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "0 4px",
                      }}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="tw-w-64">
                <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                {STATUS_OPTIONS.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={selectedStatuses.includes(status)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) => handleStatusChange(status, checked)}
                  >
                    {status}
                  </DropdownMenuCheckboxItem>
                ))}

                <DropdownMenuSeparator />

                <DropdownMenuItem active={sortDirection === "asc"} onClick={handleSortAscending}>
                  <ArrowUpNarrowWide size={14} style={{ marginRight: 8 }} />
                  Sort by Quantity (Low to High)
                </DropdownMenuItem>
                <DropdownMenuItem active={sortDirection === "desc"} onClick={handleSortDescending}>
                  <ArrowDownWideNarrow size={14} style={{ marginRight: 8 }} />
                  Sort by Quantity (High to Low)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div style={{ marginLeft: isMobile ? 0 : 20, flex: isMobile ? "1 1 100%" : "none" }}>
            <Button onClick={handleOpenOrderDialog} disabled={selectedCount === 0} style={{ width: isMobile ? "100%" : "auto" }}>
              Create Order{selectedCount > 0 ? ` (${selectedCount})` : ""}
            </Button>
          </div>
        </div>
      </div>

      {viewMode === "table" ? (
        <DataTable
          columns={columns}
          rows={pagedRows}
          totalRows={filteredData.length}
          currentPage={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showCustomNestedComponent
          customNestedComponent={customNestedComponent}
          loading={loading}
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {pagedRows.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? "200px" : "240px"}, 1fr))`,
                gap: 14,
              }}
            >
              {pagedRows.map((item) => {
                const isSelected = selectedCards.some((card) => card.id === item.id);
                const isDisabled = item?.total_quantity - item?.available_quantity === 0;

                return (
                  <div
                    key={item.id}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      borderRadius: 14,
                      border: isSelected ? "1px solid #00ADB5" : "1px solid #2a2a2e",
                      boxShadow: isSelected ? "0 0 0 1px #00ADB5 inset" : "none",
                      background: "#1e1e1e",
                      opacity: isDisabled ? 0.55 : 1,
                      overflow: "hidden",
                      transition: "border-color 0.15s",
                    }}
                  >
                    <button
                      type="button"
                      aria-label="select item"
                      disabled={isDisabled}
                      onClick={() => toggleCardSelection(item)}
                      style={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        zIndex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        border: isSelected ? "2px solid #00ADB5" : "2px solid #2a2a2e",
                        background: isSelected ? "#00ADB5" : "rgba(0,0,0,0.4)",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        color: "#00110f",
                        fontSize: 13,
                        fontWeight: 800,
                      }}
                    >
                      {isSelected ? "✓" : ""}
                    </button>

                    <div
                      onClick={() => openImageDialog(item)}
                      style={{
                        height: 130,
                        background: "#fff",
                        borderBottom: "1px solid #2a2a2e",
                        cursor: "pointer",
                      }}
                    >
                      <img
                        src={item?.item_image_url || FALLBACK_IMAGE}
                        alt={item?.item_name}
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    </div>

                    <div style={{ padding: 14 }}>
                      <div
                        title={item?.item_name}
                        style={{
                          fontWeight: 700,
                          fontSize: 13.5,
                          lineHeight: 1.3,
                          minHeight: 35,
                          color: "#fff",
                          paddingRight: 20,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {item?.item_name}
                      </div>
                      <div
                        style={{
                          fontFamily: "ui-monospace, monospace",
                          fontSize: 10,
                          color: "#c3c8cc",
                          margin: "3px 0 10px",
                        }}
                      >
                        ID: {item?.id}
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          gap: "8px 12px",
                          fontSize: 11,
                          marginBottom: 10,
                        }}
                      >
                        <FieldBlock label="UPC" value={item.upc || "N/A"} wrap={isMobile} />
                        <FieldBlock label="RETAILER" value={item.retailer_name || "N/A"} wrap={isMobile} />
                        <FieldBlock label="BUY PRICE" value={item?.buyPrice} wrap={isMobile} />
                        <FieldBlock label="CASHOUT" value={item?.cashoutPrice} color="#4caf50" wrap={isMobile} />
                        <FieldBlock
                          label="QUANTITY"
                          value={`0/${item?.total_quantity - item?.available_quantity}`}
                          wrap={isMobile}
                        />
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: "#c3c8cc", marginBottom: 3 }}>
                            STATUS
                          </div>
                          <StatusBadge status={item?.status} />
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          paddingTop: 10,
                          borderTop: "1px solid #2a2a2e",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            if (item?.item_link) {
                              window.open(item.item_link, "_blank");
                            } else {
                              showToast("Item link not available");
                            }
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            background: "none",
                            border: "none",
                            color: "#22d3ee",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          <LinkIcon size={12} style={{ marginRight: 5 }} />
                          View Product
                        </button>

                        <Button
                          size="xs"
                          disabled={isDisabled}
                          onClick={() => toggleCardSelection(item)}
                        >
                          {isSelected ? "✓ Added" : "+ Add"}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                height: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px dashed rgba(255,255,255,0.12)",
                borderRadius: 14,
                color: "#c3c8cc",
                fontSize: 13,
              }}
            >
              {loading ? "Loading…" : "No data found"}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginTop: 16, flexWrap: "wrap", gap: isMobile ? 10 : 0 }}>
            <div style={{ display: "flex", alignItems: "center", marginRight: isMobile ? 0 : 16 }}>
              <span style={{ fontSize: 12, color: "#c3c8cc", marginRight: 8 }}>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))}
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 6,
                  color: "#fff",
                  fontSize: 12,
                  padding: "3px 6px",
                }}
              >
                {[4, 8, 12, 24, 48].map((n) => (
                  <option key={n} value={n} style={{ color: "#000" }}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <span style={{ fontSize: 12, color: "#c3c8cc", marginRight: isMobile ? 0 : 16 }}>
              {filteredData.length === 0 ? 0 : page * rowsPerPage + 1}–
              {Math.min((page + 1) * rowsPerPage, filteredData.length)} of {filteredData.length}
            </span>
            <div style={{ display: "flex" }}>
              <button
                type="button"
                disabled={page === 0}
                onClick={() => handleChangePage(page - 1)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  marginRight: 4,
                  borderRadius: 7,
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.08)",
                  color: page === 0 ? "rgba(255,255,255,0.25)" : "#fff",
                  cursor: page === 0 ? "not-allowed" : "pointer",
                }}
              >
                <ChevronDown size={15} style={{ transform: "rotate(90deg)" }} />
              </button>
              <button
                type="button"
                disabled={(page + 1) * rowsPerPage >= filteredData.length}
                onClick={() => handleChangePage(page + 1)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.08)",
                  color:
                    (page + 1) * rowsPerPage >= filteredData.length ? "rgba(255,255,255,0.25)" : "#fff",
                  cursor: (page + 1) * rowsPerPage >= filteredData.length ? "not-allowed" : "pointer",
                }}
              >
                <ChevronDown size={15} style={{ transform: "rotate(-90deg)" }} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberPreQuotedCooksPage;
